<div class="flex flex-col h-full">

  <!-- Estado de carregamento inicial -->
  <div *ngIf="loading" class="flex flex-1 flex-col items-center justify-center py-24">
    <img src="assets/img/Loading.svg" alt="Carregando..." class="w-24 h-24 md:w-28 md:h-28 mb-5">
    <p class="text-base font-medium text-gray-700">Carregando intercorrência...</p>
    <p class="text-sm text-gray-500 mt-1">Estamos buscando os dados do plano e do relatório.</p>
  </div>

  <!-- Erro de carregamento -->
  <div *ngIf="!loading && loadError" class="flex flex-col items-center justify-center py-20 text-center">
    <fa-icon [icon]="faExclamationTriangle" class="text-yellow-500 text-3xl mb-3"></fa-icon>
    <h3 class="text-lg font-semibold text-gray-700 mb-1">Não foi possível carregar a intercorrência</h3>
    <p class="text-gray-500 mb-4">Tente novamente em instantes.</p>
    <button (click)="goBack()" class="px-4 py-2 text-sm font-medium text-white bg-westetics-purple-800 rounded-lg hover:bg-westetics-purple-600">
      Voltar
    </button>
  </div>

  <!-- Conteúdo principal -->
  <ng-container *ngIf="!loading && !loadError">

    <!-- HEADER UNIFICADO DA INTERCORRÊNCIA -->
    <div class="bg-white rounded-lg border border-gray-200 p-4 mb-6">
      <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">

        <!-- Lado esquerdo: voltar + título + badges + label -->
        <div class="flex items-start gap-4 min-w-0">
          <button (click)="goBack()"
            class="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full text-westetics-purple-800 hover:bg-westetics-purple-50 transition-colors mt-0.5"
            title="Voltar para o plano">
            <fa-icon [icon]="faArrowLeft"></fa-icon>
          </button>
          <div class="min-w-0">
            <span class="text-lg font-semibold text-westetics-neutral-950">Intercorrência</span>
            <div class="flex items-center gap-2 flex-wrap mt-0.5">
              <span *ngIf="treatmentPlan?.patient_name" class="text-sm text-westetics-neutral-600 truncate">
                {{ treatmentPlan?.patient_name }}
              </span>
              <span *ngIf="treatmentPlan?.patient_name" class="text-gray-300 text-sm">•</span>
              <span class="px-2 py-0.5 rounded-full text-xs font-medium" [ngClass]="statusBadgeClass">
                {{ statusBadgeText }}
              </span>
              <ng-container *ngIf="incidentStatus === 'CLOSED'">
                <span class="text-gray-300 text-sm">•</span>
                <span class="text-xs px-2 py-0.5 rounded-full font-medium bg-westetics-purple-100 text-westetics-purple-800">
                  <fa-icon [icon]="signatureStatus === 'signed' ? faCheck : faExclamationTriangle" class="mr-1 text-[10px]"></fa-icon>
                  {{ signatureStatusText }}
                </span>
              </ng-container>
            </div>
            <p *ngIf="incidentStatus === 'CLOSED' && signatureStatus === 'pending'"
              class="text-xs text-gray-500 mt-1">
              Documento enviado para o e-mail legal da clínica. Aguardando assinatura digital pela Autentique.
            </p>
          </div>
        </div>

        <!-- Lado direito: indicador + autentique + ? + salvar + finalizar -->
        <div class="flex flex-wrap items-center gap-2 md:gap-3 shrink-0">

          <!-- Indicador de save -->
          <div *ngIf="!isReadOnly && saveIndicatorText"
            class="text-xs md:text-sm px-2 py-1 rounded-full"
            [ngClass]="{
              'text-westetics-purple-800': saveState === 'saving',
              'text-green-700 bg-green-50': saveState === 'saved',
              'text-red-700 bg-red-50 border border-red-200': saveState === 'error',
              'text-gray-700 bg-gray-100': saveState === 'unsaved'
            }">
            <span *ngIf="saveState === 'saving'" class="flex items-center gap-1.5">
              <app-loading-spinner size="xs"></app-loading-spinner> {{ saveIndicatorText }}
            </span>
            <span *ngIf="saveState !== 'saving'">{{ saveIndicatorText }}</span>
          </div>

          <!-- Abrir link do Autentique -->
          <button *ngIf="incidentStatus === 'CLOSED' && signatureStatus === 'pending'"
            (click)="openSigningLink()"
            class="p-2 rounded-lg text-westetics-purple-800 hover:text-westetics-purple-600 transition-colors"
            title="Abrir no Autentique">
            <fa-icon [icon]="faPaperPlane"></fa-icon>
          </button>

          <!-- Botão Ajuda -->
          <button (click)="toggleHelp()"
            class="bg-westetics-purple-800 hover:bg-westetics-purple-600 text-white font-medium w-8 h-8 rounded-full flex items-center justify-center text-sm"
            title="Ajuda">
            <fa-icon [icon]="faQuestion"></fa-icon>
          </button>

          <!-- Salvar (rascunho) -->
          <button *ngIf="!isReadOnly && incidentId"
            (click)="onSaveClick()"
            [disabled]="saving || finalizing"
            class="px-4 py-2 text-sm font-medium text-westetics-purple-800 bg-white border border-westetics-purple-800 rounded-lg hover:bg-westetics-purple-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {{ saving ? 'Salvando...' : 'Salvar Intercorrência' }}
          </button>

          <!-- Finalizar (rascunho) -->
          <button *ngIf="!isReadOnly && incidentId"
            (click)="onFinalizeClick()"
            [disabled]="saving || finalizing"
            class="px-4 py-2 text-sm font-medium text-white bg-westetics-purple-800 rounded-lg hover:bg-westetics-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {{ finalizing ? 'Finalizando...' : 'Finalizar Intercorrência' }}
          </button>

        </div>
      </div>
    </div>

    <!-- Banner: clínica sem perguntas configuradas -->
    <div *ngIf="!hasClinicQuestions" class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
      <div class="flex items-start gap-3">
        <fa-icon [icon]="faExclamationTriangle" class="text-yellow-500 text-lg mt-0.5"></fa-icon>
        <div>
          <h3 class="text-sm font-semibold text-yellow-800">Perguntas não configuradas</h3>
          <p class="text-sm text-yellow-700 mt-1">
            Sua clínica ainda não possui perguntas de intercorrência personalizadas.
            Você pode preencher apenas as perguntas fixas (Data, Procedimento e Área tratada).
            Para configurar perguntas adicionais, entre em contato com o administrador.
          </p>
        </div>
      </div>
    </div>

    <!-- Formulário (renderizado apenas quando a intercorrência existe) -->
    <div *ngIf="incidentId" class="bg-white rounded-lg shadow-md p-4 md:p-6">
      <app-incident-form
        [sections]="incidentSections"
        [existingAnswers]="existingAnswers"
        [existingAttachments]="existingAttachments"
        [fixedValues]="incidentFixedValues"
        [dynamicOptions]="incidentDynamicOptions"
        [regionSelectorConfig]="incidentRegionSelectorConfig"
        [treatmentStatus]="treatmentPlan?.status || 'ACTIVE'"
        [incidentStatus]="incidentStatus"
        [incidentId]="incidentId || ''"
        [treatmentPlanId]="treatmentPlanId"
        [saving]="saving"
        [finalizing]="finalizing"
        [hideActions]="true"
        (formChange)="onFormChange($event)">
      </app-incident-form>
    </div>

    <!-- Estado vazio: plano ativo, sem intercorrência, modal de criação aberto/dismissed -->
    <div *ngIf="!incidentId && treatmentPlan?.status === 'ACTIVE' && !showCreateConfirm"
      class="bg-white rounded-lg shadow-md p-8 text-center">
      <div class="w-14 h-14 mx-auto bg-westetics-purple-50 rounded-full flex items-center justify-center mb-3">
        <i class="fas fa-clipboard-list text-westetics-purple-800 text-xl"></i>
      </div>
      <h3 class="text-base font-semibold text-gray-800 mb-1">Nenhuma intercorrência registrada</h3>
      <p class="text-sm text-gray-500 mb-4">Este plano de tratamento ainda não possui intercorrência.</p>
      <button (click)="showCreateConfirm = true"
        class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-westetics-purple-800 rounded-lg hover:bg-westetics-purple-600 transition-colors">
        <span class="bg-white text-westetics-purple-800 rounded-full w-5 h-5 flex items-center justify-center">
          <span class="font-bold text-sm">+</span>
        </span>
        Criar Intercorrência
      </button>
    </div>

    <!-- Estado vazio: plano fechado e nunca teve intercorrência -->
    <div *ngIf="!incidentId && treatmentPlan?.status === 'CLOSED'"
      class="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
      <p class="text-sm">Nenhuma intercorrência registrada para este plano.</p>
    </div>

  </ng-container>

  <!-- Modal de confirmação de criação de intercorrência -->
  <div *ngIf="showCreateConfirm"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" (click)="$event.stopPropagation()">
      <div class="flex items-start gap-3 mb-4">
        <div class="w-10 h-10 rounded-full bg-westetics-purple-50 flex items-center justify-center shrink-0">
          <i class="fas fa-clipboard-list text-westetics-purple-800"></i>
        </div>
        <div>
          <h3 class="text-lg font-semibold text-gray-900">Criar intercorrência?</h3>
          <p class="text-sm text-gray-600 mt-1">
            Este plano de tratamento ainda não possui intercorrência registrada.
            Deseja criar uma nova intercorrência agora?
          </p>
          <p class="text-xs text-gray-500 mt-2">
            A intercorrência será criada como rascunho. Você poderá preencher aos poucos —
            o salvamento é automático.
          </p>
        </div>
      </div>
      <div class="flex justify-end gap-2 mt-6">
        <button (click)="cancelCreateIncident()" [disabled]="creating"
          class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50">
          Cancelar
        </button>
        <button (click)="confirmCreateIncident()" [disabled]="creating"
          class="px-4 py-2 text-sm font-medium text-white bg-westetics-purple-800 rounded-lg hover:bg-westetics-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          {{ creating ? 'Criando...' : 'Criar Intercorrência' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Modal de Ajuda — conteúdo da intercorrência -->
  <div *ngIf="showHelpInfo"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    (click)="showHelpInfo = false">
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col" style="max-height: 85vh;"
      (click)="$event.stopPropagation()">

      <!-- Header do modal -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
        <div class="flex items-center gap-2">
          <i class="fas fa-info-circle text-westetics-purple-800 text-lg"></i>
          <h3 class="text-lg font-semibold text-gray-900">Sobre a Intercorrência</h3>
        </div>
        <button type="button" (click)="showHelpInfo = false"
          class="text-gray-400 hover:text-gray-600 transition-colors">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <!-- Corpo do modal — scroll independente -->
      <div class="flex-1 min-h-0 overflow-y-auto px-6 py-4 text-sm text-gray-700 space-y-5">

        <!-- O que é -->
        <section>
          <h4 class="font-semibold text-gray-900 mb-1">O que é uma intercorrência?</h4>
          <p>
            É um evento adverso, complicação ou efeito não esperado que ocorra <strong>durante ou após</strong>
            um procedimento estético do plano de tratamento. Documentar corretamente é importante para o histórico
            clínico do paciente, suporte legal em caso de questionamento e aprendizado para futuras intervenções.
          </p>
        </section>

        <!-- Regras gerais -->
        <section>
          <h4 class="font-semibold text-gray-900 mb-1">Regras gerais</h4>
          <ul class="list-disc ml-5 space-y-1">
            <li>Cada plano de tratamento pode ter <strong>apenas uma</strong> intercorrência.</li>
            <li>O relatório nasce como rascunho assim que você abre esta tela.</li>
            <li>Você pode preencher aos poucos — todas as alterações são salvas automaticamente.</li>
            <li>Apenas ao <strong>Finalizar</strong> o documento é bloqueado e enviado para assinatura.</li>
            <li>Se o plano de tratamento for encerrado, a intercorrência também fica somente leitura.</li>
          </ul>
        </section>

        <!-- Como preencher -->
        <section>
          <h4 class="font-semibold text-gray-900 mb-1">Como preencher</h4>
          <p class="mb-2">As 3 perguntas fixas no topo:</p>
          <ol class="list-decimal ml-5 space-y-1 mb-2">
            <li><strong>Data do procedimento</strong> — pré-preenchida com a data do plano (ajuste se necessário).</li>
            <li><strong>Procedimento da intercorrência</strong> — qual procedimento contratado originou o evento.</li>
            <li><strong>Área tratada</strong> — região(ões) facial/corporal afetada(s); pré-selecionada com base nos produtos do plano.</li>
          </ol>
          <p>
            As demais seções (cronologia, gravidade, tratamento, exames, encaminhamentos, etc.) são configuradas
            pelo administrador da clínica. Campos marcados com <span class="text-red-500 font-semibold">*</span>
            são obrigatórios apenas para finalizar.
          </p>
        </section>

        <!-- Anexos -->
        <section>
          <h4 class="font-semibold text-gray-900 mb-1">Anexos</h4>
          <ul class="list-disc ml-5 space-y-1">
            <li>Aceitos: imagens (JPG, PNG, GIF, WebP, HEIC), PDF, Word, PowerPoint e vídeos (MP4, MOV, WebM, AVI).</li>
            <li>Tamanho máximo: <strong>100 MB por arquivo</strong>.</li>
            <li>Arraste para reordenar antes de finalizar — a ordem é preservada no PDF.</li>
            <li>Imagens são embutidas no PDF; outros tipos aparecem como referência.</li>
          </ul>
        </section>

        <!-- Salvamento -->
        <section>
          <h4 class="font-semibold text-gray-900 mb-1">Salvamento automático</h4>
          <ul class="list-disc ml-5 space-y-1">
            <li>A cada alteração, o rascunho é salvo automaticamente após cerca de 1 segundo de inatividade.</li>
            <li>O indicador no canto superior direito mostra o estado: <em>Salvando…</em> / <em>Salvo às HH:MM</em> / <em>Erro ao salvar</em>.</li>
            <li>Você pode fechar a aba a qualquer momento — nada é perdido.</li>
            <li>Para forçar um save imediato, clique em <strong>Salvar Intercorrência</strong>.</li>
          </ul>
        </section>

        <!-- Finalização -->
        <section>
          <h4 class="font-semibold text-gray-900 mb-1">Finalização</h4>
          <p class="mb-2">Ao clicar em <strong>Finalizar Intercorrência</strong>, o sistema:</p>
          <ol class="list-decimal ml-5 space-y-1 mb-2">
            <li>Valida que todos os campos obrigatórios (com *) estão preenchidos.</li>
            <li>Gera um PDF consolidando respostas e anexos.</li>
            <li>Envia o PDF para o e-mail legal da clínica via <strong>Autentique</strong> para assinatura digital.</li>
            <li>Marca o relatório como <strong>Finalizada</strong> — bloqueando edições futuras.</li>
          </ol>
          <p class="text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2.5">
            <i class="fas fa-exclamation-triangle mr-1"></i>
            Esta ação é <strong>irreversível</strong>. Revise tudo antes de finalizar.
          </p>
        </section>

        <!-- Pré-requisitos -->
        <section>
          <h4 class="font-semibold text-gray-900 mb-1">Pré-requisitos para finalizar</h4>
          <p class="mb-1">A clínica precisa ter configurado no perfil da empresa:</p>
          <ul class="list-disc ml-5 space-y-1">
            <li><strong>E-mail legal</strong> — destinatário da assinatura digital.</li>
            <li><strong>Pasta na Autentique</strong> vinculada à clínica.</li>
          </ul>
          <p class="mt-1">Se faltar algum, o sistema mostra erro claro indicando o que configurar.</p>
        </section>

        <!-- Após finalizada -->
        <section>
          <h4 class="font-semibold text-gray-900 mb-1">Após finalizada</h4>
          <ul class="list-disc ml-5 space-y-1">
            <li><strong>Abrir no Autentique</strong> — abre o documento na Autentique para acompanhar ou assinar.</li>
          </ul>
        </section>

        <!-- Boas práticas -->
        <section>
          <h4 class="font-semibold text-gray-900 mb-1">Boas práticas de documentação</h4>
          <ul class="list-disc ml-5 space-y-1">
            <li>Seja <strong>objetivo e factual</strong>: o que aconteceu, quando, como foi tratado e qual a resposta clínica.</li>
            <li>Evite julgamentos subjetivos — descreva fatos, não impressões.</li>
            <li>Anexe fotos e exames sempre que possível: fortalecem o relatório.</li>
            <li>Preencha o quanto antes — memória recente é mais precisa.</li>
            <li>Em caso de dúvida sobre gravidade ou conduta, escale para o profissional responsável <em>antes</em> de finalizar.</li>
          </ul>
        </section>
      </div>

      <!-- Footer do modal -->
      <div class="px-6 py-3 border-t border-gray-200 bg-gray-50 shrink-0">
        <button type="button" (click)="showHelpInfo = false"
          class="w-full px-4 py-2 text-sm font-medium bg-westetics-purple-800 text-white rounded-lg hover:bg-westetics-purple-900 focus:outline-none transition-colors">
          Entendi
        </button>
      </div>
    </div>
  </div>

  <!-- Modal de confirmação de finalização -->
  <div *ngIf="showFinalizeConfirm"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    (click)="cancelFinalize()">
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" (click)="$event.stopPropagation()">
      <div class="flex items-start gap-3 mb-4">
        <div class="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
          <fa-icon [icon]="faExclamationTriangle" class="text-amber-600"></fa-icon>
        </div>
        <div>
          <h3 class="text-lg font-semibold text-gray-900">Finalizar intercorrência?</h3>
          <p class="text-sm text-gray-600 mt-1">
            Após finalizar, o relatório será bloqueado para edição e enviado para assinatura digital
            via Autentique. Esta ação é <strong>irreversível</strong>.
          </p>
        </div>
      </div>
      <div class="flex justify-end gap-2 mt-6">
        <button (click)="cancelFinalize()"
          class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
          Cancelar
        </button>
        <button (click)="confirmFinalize()" [disabled]="finalizing"
          class="px-4 py-2 text-sm font-medium text-white bg-westetics-purple-800 rounded-lg hover:bg-westetics-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          {{ finalizing ? 'Finalizando...' : 'Confirmar finalização' }}
        </button>
      </div>
    </div>
  </div>

</div>
